import { GenericConfirmModal } from "@/components/common/GenericConfirmModal";
import { SendMessageFn } from "@/components/communications-chat-components/ChatContainer";
import FilesContainer from "@/components/communications-chat-components/FilesContainer";
import { CHANNELS } from "@/components/communications-chat-components/portal/PortalChatContainer";
import { mapToNewListWithUpdatedTarget as updateTargetedChat } from "@/components/communications-chat-components/portal/PortalChatContext";
import { UnreadMessagesCountBadge } from "@/components/communications-chat-components/portal/PortalChatListItem";
import { DEFAULT_FORMATS, DEFAULT_TOOLBAR_OPTIONS } from "@/components/utils/Constants";
import LazoTooltip from "@/components/utils/LazoTooltip";
import { LAZO_TENANT_EXTERNAL_ID } from "@/contexts/TenantContext";
import { Colors } from "@/enums/Colors";
import { LazoThreadConversationStatus } from "@/enums/LazoThreadConversationStatus";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { LazoChatConversationShell } from "@/models/ai-chat/LazoChatConversation";
import { SmallConversationMember } from "@/models/ai-chat/SmallConversationMember";
import LazoUserService from "@/services/LazoUserService";
import LazoChatConversationService from "@/services/ai-chat/LazoChatConversationService";
import LazoChatMessageService from "@/services/ai-chat/LazoChatMessageService";
import { getValidFiles } from "@/utils/LazoFIleUtils";
import { AttachFileOutlined } from "@mui/icons-material";
import { Box, Button, Divider, IconButton, SxProps, Typography, styled } from "@mui/material";
import Picker, { EmojiClickData } from "emoji-picker-react";
import dynamic from "next/dynamic";
import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import emoji from "../../../../assets/images/EmojiIcon.svg";
import arrowDown from "../../../../assets/images/arrowDown.svg";
import PaperPlaneOutlined from "../../../../assets/images/paper-plane-send-icon.svg";
import warning from "../../../../assets/images/warning2.svg";
const QuillEditor = dynamic(() => import("@/components/task-components/QuillEditor"), { ssr: false });

interface ChatTextFieldProps {
  onSendMessage: SendMessageFn;
  selectedChat: LazoChatConversationShell;
  onChatSelect: (chat: LazoChatConversationShell) => void;
  internalChat: LazoChatConversationShell;
  generalChat: LazoChatConversationShell;
  setGeneralChat: Dispatch<SetStateAction<LazoChatConversationShell | null>>;
  setChatChannel: (channel: CHANNELS) => void;
  chatChannel: CHANNELS;
  setChats: Dispatch<SetStateAction<LazoChatConversationShell[]>>;
  members: SmallConversationMember[];
  loading: boolean;
}

export const InboxChatTextFieldComponent = ({
  onSendMessage,
  selectedChat,
  onChatSelect,
  internalChat,
  generalChat,
  setGeneralChat,
  setChatChannel,
  chatChannel,
  setChats,
  members,
  loading,
}: ChatTextFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const pickerRef = useRef(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [mentions, setMentions] = useState<{ id: string }[]>([]);
  const [lastTypingTime, setLastTypingTime] = useState<number>(0);
  const [openEmojiPicker, setOpenEmojiPicker] = useState<boolean>(false);
  const [textboxContent, setTextboxContent] = useState<{ formatted: string; raw: string }>({ formatted: "", raw: "" });
  const [confirmFinalizeAnchor, setConfirmFinalizeAnchor] = useState<HTMLDivElement | null>(null);
  const [finalizing, setFinalizing] = useState<boolean>(false);

  const hasInternalChat = !!generalChat?.internalChannelExternalId;
  const isInternalMember = members.find((cm) => cm.userExternalId === AuthService.getUser().externalId)?.isInternal;

  const hasInternalUnreadMessages = generalChat?.internalUnreadMessageCount > 0;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleChatChannel = (channel: CHANNELS) => {
    setChatChannel(channel);
    if (channel === CHANNELS.TEAM) {
      onChatSelect(internalChat!);
      if (generalChat.internalUnreadMessageCount > 0) {
        setGeneralChat((prev) => ({ ...prev, internalUnreadMessageCount: 0 }));
      }
    } else {
      onChatSelect(generalChat);
    }
  };

  const handleOpenEmojiPicker = () => {
    setOpenEmojiPicker(!openEmojiPicker);
  };

  useEffect(() => {
    setOpenEmojiPicker(false);
    setTextboxContent({ raw: "", formatted: "" });
  }, [selectedChat?.externalId]);

  const handleChange = (event: { target: { name: string; value: string } }, text: string) => {
    setTextboxContent({
      formatted: event.target.value,
      raw: text,
    });

    if (selectedChat?.externalId && new Date().getTime() - lastTypingTime >= 3000) {
      LazoChatMessageService.sendTypingEvent(selectedChat?.externalId as string, !!!text || text.trim() !== "");
      setLastTypingTime(new Date().getTime());
    }
  };

  function handleSendMessage() {
    const isEmpty = !textboxContent.raw || textboxContent.raw.trim() === "";
    if (isEmpty && files.length <= 0) return;
    onSendMessage(isEmpty ? null : textboxContent.formatted, selectedChat, chatChannel === CHANNELS.REPLY ? null : mentions, files);
    setTextboxContent({ raw: "", formatted: "" });
    setFiles([]);
  }

  const handleEmojiClick = (emojiObject: EmojiClickData) => {
    setTextboxContent((prev) => ({
      formatted: prev.formatted + emojiObject.emoji,
      raw: prev.raw + emojiObject.emoji,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = getValidFiles(Array.from(e.target.files));
      setFiles([...files, ...newFiles]);
      e.target.value = "";
    }
  };
  const handleAttachFile = () => {
    fileInputRef.current?.click();
  };

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setOpenEmojiPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (openEmojiPicker && pickerRef.current) {
      pickerRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [openEmojiPicker]);

  function onFilePaste(f: File): void {
    setFiles((prev) => [...prev, f]);
  }

  async function suggestEmployees(term: string) {
    return LazoUserService.searchUserByTermAndTenant(LAZO_TENANT_EXTERNAL_ID, term, 0, 20);
  }

  function renderMentionListItem(item: { id: string; value: string; email: string }) {
    const mainContainer = document.createElement("div");
    mainContainer.classList.add("mention-item-container");

    const nameContainer = document.createElement("div");
    nameContainer.innerText = item.value;
    nameContainer.classList.add("mention-item-name");

    mainContainer.appendChild(nameContainer);

    const mailContainer = document.createElement("div");
    mailContainer.innerText = item.email;
    mailContainer.classList.add("mention-item-email");

    mainContainer.appendChild(mailContainer);

    return mainContainer;
  }

  function onFinalizeConversationClick(ev: React.MouseEvent<HTMLDivElement>) {
    setConfirmFinalizeAnchor(ev.currentTarget);
  }

  function onConfirmFinalizeConversation() {
    setFinalizing(true);
    LazoChatConversationService.finalizeThread(generalChat.externalId)
      .then((r) => {
        r.onSuccess(() => {
          setConfirmFinalizeAnchor(null);
          setGeneralChat((prev) => ({ ...prev, currentThreadStatus: LazoThreadConversationStatus.FINALIZED }));
          setChats((c) => updateTargetedChat(c, generalChat.externalId, { currentThreadStatus: LazoThreadConversationStatus.FINALIZED }));
        });
        r.onError((m) => NotificationService.error(m));
      })
      .finally(() => setFinalizing(false));
  }

  return (
    <>
      <ChatContainer>
        {files.length > 0 && (
          <FilesContainer
            textSx={{ maxWidth: "75%" }}
            style={{ position: "auto", backgroundColor: "transparent" }}
            fileContainerStyle={{ backgroundColor: "white" }}
            files={files}
            setFiles={setFiles}
          />
        )}
        {chatChannel === CHANNELS.TEAM && (
          <Box sx={LazoTeamTextContainer}>
            <img alt={"warning-img"} src={warning.src} style={{ color: "white", marginRight: "3px", display: "flex" }}></img> You are
            addressing Lazo Team, only you and team members tagged here can see this.
          </Box>
        )}
        <ChatTextAreaContainer>
          {loading && (
            <Box display="flex" flex="1" alignItems="center" justifyContent="center">
              <Spinner size="1.875rem" sx={{ color: Colors.LAZO_PURPLE }} />
            </Box>
          )}
          {!loading ? (
            isInternalMember ? (
              <>
                <InternalMemberMessageContainer>
                  <Typography>Read Only</Typography>
                </InternalMemberMessageContainer>
                <ButtonsContainer>
                  <Typography sx={{ display: "flex", alignContent: "start", gap: "10px" }}>
                    <StyledButton onClick={() => handleChatChannel(CHANNELS.REPLY)} selected={chatChannel === CHANNELS.REPLY}>
                      Reply
                    </StyledButton>
                    {hasInternalChat && (
                      <StyledButton onClick={() => handleChatChannel(CHANNELS.TEAM)} selected={chatChannel === CHANNELS.TEAM}>
                        Lazo Team
                      </StyledButton>
                    )}
                  </Typography>
                </ButtonsContainer>
              </>
            ) : (
              <>
                <QuillContainer translate="no">
                  <QuillEditor
                    value={textboxContent.formatted}
                    onChange={handleChange}
                    onFilePaste={onFilePaste}
                    onMentionsChange={setMentions}
                    options={{
                      formats: [...DEFAULT_FORMATS, "mention"],
                      modules: {
                        toolbar: DEFAULT_TOOLBAR_OPTIONS,
                        mention: {
                          allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
                          mentionDenotationChars: ["@"],
                          source: async function (searchTerm: string, renderList: any) {
                            const promise = await suggestEmployees(searchTerm);
                            const page = promise.getContentOrElse(() => ({ content: [], pageNumber: 0, totalElements: 0 }));
                            renderList(
                              page.content.map((s) => ({
                                id: s.externalId,
                                value: s.user.firstName + " " + s.user.lastName,
                                email: s.user.email,
                                lazoEmployee: true,
                              }))
                            );
                          },
                          renderItem: renderMentionListItem,
                        },
                      },
                      placeholder: "Type your message...",
                      theme: "snow",
                    }}
                  />
                </QuillContainer>
                <ButtonsContainer>
                  <Box display="flex" alignItems="center" gap="0.2rem">
                    <LazoTooltip title="10 MB maximum file size">
                      <IconButton onClick={handleAttachFile}>
                        <input ref={fileInputRef} id="file" type="file" multiple style={{ display: "none" }} onChange={handleFileChange} />
                        <AttachFileOutlined
                          sx={{
                            transform: "scale(1.2) rotate(40deg)",
                            ":hover": { cursor: "pointer" },
                          }}
                        />
                      </IconButton>
                    </LazoTooltip>
                    <IconButton onClick={handleOpenEmojiPicker}>
                      <img alt={"emoji-img"} src={emoji.src} height={"16px"} />
                    </IconButton>
                    <Typography sx={{ display: "flex", alignContent: "start", gap: "10px" }}>
                      <StyledButton onClick={() => handleChatChannel(CHANNELS.REPLY)} selected={chatChannel === CHANNELS.REPLY}>
                        Reply
                      </StyledButton>
                      {hasInternalChat && (
                        <StyledButton onClick={() => handleChatChannel(CHANNELS.TEAM)} selected={chatChannel === CHANNELS.TEAM}>
                          {hasInternalUnreadMessages && (
                            <UnreadMessagesCountBadge
                              className="orange"
                              sx={{ position: "absolute", left: 0, bottom: 10 }}
                              badgeContent={generalChat.internalUnreadMessageCount}
                            ></UnreadMessagesCountBadge>
                          )}
                          Lazo Team
                        </StyledButton>
                      )}
                    </Typography>
                    {chatChannel === CHANNELS.REPLY &&
                      generalChat?.currentThreadStatus &&
                      generalChat.currentThreadStatus !== LazoThreadConversationStatus.FINALIZED && (
                        <FinalizeConversationButtonContainer onClick={onFinalizeConversationClick}>
                          <Typography fontSize="12px" fontWeight="600">
                            Finalize Conversation Thread
                          </Typography>
                        </FinalizeConversationButtonContainer>
                      )}
                  </Box>
                  <RightButtonsContainer>
                    <IconButton onClick={handleSendMessage}>
                      <img alt={"paper-plane-img"} src={PaperPlaneOutlined.src} />
                    </IconButton>

                    <Divider orientation="vertical" flexItem />

                    <IconButton sx={{}}>
                      <img alt={"arrow-down-img"} src={arrowDown.src} />
                    </IconButton>
                  </RightButtonsContainer>
                </ButtonsContainer>
              </>
            )
          ) : (
            <></>
          )}
        </ChatTextAreaContainer>
      </ChatContainer>
      <div ref={pickerRef}>
        {openEmojiPicker && (
          <Picker
            onEmojiClick={handleEmojiClick}
            style={{ position: "relative" }}
            lazyLoadEmojis={true}
            searchDisabled={true}
            height={300}
            width={"100%"}
          />
        )}
      </div>
      <GenericConfirmModal
        text={"Are you sure you want to finalize this conversation thread?"}
        anchor={confirmFinalizeAnchor}
        setAnchorElement={setConfirmFinalizeAnchor}
        onConfirm={onConfirmFinalizeConversation}
        loading={finalizing}
      />
    </>
  );
};

const ChatContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  backgroundColor: "transparent",
  zIndex: 1,
  borderRadius: "1.250rem",
  position: "relative",
  minHeight: "10rem",
  gap: "0.25rem",
});

const ChatTextAreaContainer = styled("div")({
  position: "relative",
  display: "flex",
  flex: 1,
  flexDirection: "column",
  width: "100%",
  overflowWrap: "break-word",
  borderRadius: "1.250rem",
  border: "1px solid #e0e0e0",
  zIndex: 3,
  backgroundColor: "#fff",
  height: "100%",
  minHeight: "10rem",
});

const RightButtonsContainer = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  right: "0",
  bottom: "0",
  zIndex: 2,
});

const StyledButton = styled(Button)<{ selected: boolean }>(({ selected }) => ({
  position: "relative",
  color: selected ? Colors.BLACK : Colors.LIGHT_GRAY_8A8E93,
  border: `2px solid transparent`,
  borderTop: selected ? `2px solid ${Colors.BLACK}` : "none",
  fontSize: "10px",
  fontWeight: selected ? "600" : "500",
  textTransform: "none",
}));

const LazoTeamTextContainer: SxProps = {
  background: "linear-gradient(180deg, #8D8BFF 0%, #5346E9 100%)",
  borderRadius: "1.25rem",
  padding: "0.625rem",
  color: "white",
  fontSize: "0.812rem",
  fontWeight: "500",
  width: "100%",
  display: "flex",
  justifyContent: "start",
  position: "relative",
  zIndex: 1,
};

const QuillContainer = styled("div")(() => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",

  ".ql-toolbar.ql-snow": {
    border: 0,
    borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  },

  ".ql-container.ql-snow": {
    border: 0,
  },

  ".ql-editor": {
    maxHeight: "150px",
    overflowY: "auto !important",
    minHeight: "60px",
  },

  ".ql-mention-list-container": {
    width: "16.875rem",
    border: "1px solid #f0f0f0",
    borderRadius: "0.250rem",
    backgroundColor: "#ffffff",
    boxShadow: "0 2px 12px 0 rgba(30, 30, 30, 0.08)",
    zIndex: "9001",
    overflow: "autp",
  },

  ".ql-mention-loading": {
    padding: "0 0.5rem",
    verticalAlign: "middle",
    fontSize: "1.000rem",
  },

  ".ql-mention-list": {
    listStyle: "none",
    margin: 0,
    padding: 0,
    overflow: "hidden",
  },

  ".ql-mention-list-item": {
    cursor: "pointer",
    padding: "0.375rem",
    verticalAlign: "middle",
    borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  },

  ".ql-mention-list-item.disabled": {
    cursor: "auto",
  },

  ".ql-mention-list-item.selected": {
    backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
    textDecoration: "none",
    fontWeight: "600",
  },

  ".mention": {
    width: "4.062rem",
    borderRadius: "0.375rem",
    backgroundColor: Colors.BUTTON_SECONDARY_HOVER_PURPLE,
    padding: "0.188rem 0",
    marginRight: "0.125rem",
    userSelect: "all",
  },

  ".mention > span": {
    margin: "0 0.188rem",
  },

  ".mention-item-name": {
    fontSize: "14px",
  },
  ".mention-item-email": {
    fontSize: "12px",
  },

  ".mention-item-container": {
    display: "flex",
    flexDirection: "column",
    gap: "0.125rem",
  },
}));

const ButtonsContainer = styled(Box)(() => ({
  justifyContent: "space-between",
  width: "100%",
  display: "flex",
  padding: "0",
  gap: "0.3rem",
}));

const FinalizeConversationButtonContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0.1rem 0.5rem",
  borderRadius: "2rem",
  transition: "all 0.125s ease-out",
  backgroundColor: Colors.ACCENT_ORANGE,
  border: `1px solid ${Colors.DARKER_DARKER_GRAY}`,
  cursor: "pointer",
  ":hover": {
    backgroundColor: Colors.ORANGE_FF9A3D,
  },
}));

const InternalMemberMessageContainer = styled(Box)(() => ({
  display: "flex",
  flex: 1,
  alignItems: "center",
  justifyContent: "center",
}));

export default InboxChatTextFieldComponent;
